import React, { useEffect, useState } from 'react'
import { Content } from './style'
import LoadingComponent from '../../../components/LoadingComponent'
import { LeftSide, SectionName, SubHeaderContainer } from '../../../components/styles/subheader-style'
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import certificate from '../../../assets/certificates/SubHeader/certificate.svg'
import param from '../../../assets/certificates/SubHeader/param.svg'
import EditStructureModal from '../../../components/Modals/EditStructure'
import { ParamType } from './CreateParam'
import { Filter, Header } from '../../../components/SubHeaderFilter/types'
import { TypeMainOutlet } from '..'
import ApiService from '../../../services/ApiService'
import { PageHeader } from '../../../components/StandardPage'
import { usePersistedFilters } from '../../../hooks/usePersistedFilters'

export interface CertificateOutletType {
  setModal: React.Dispatch<React.SetStateAction<boolean>>
  param: ParamType
  setParam: React.Dispatch<React.SetStateAction<ParamType>>
  setClickedStructure: React.Dispatch<React.SetStateAction<number | null>>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  appliedFilters: Filter[]
  search: string
  refresh: boolean
  setLoadingHeader: React.Dispatch<React.SetStateAction<boolean>>
}

const paramTableHeaders: Header[] = [
  { key: 'name', label: 'Nome do parâmetro', type: 'string' },
  { key: 'class', label: 'Classe', type: 'string' },
  { key: 'family', label: 'Família', type: 'string' },
  { key: 'astm', label: 'ASTM', type: 'string' },
  { key: 'uns', label: 'UNS', type: 'string' },
  { key: 'din', label: 'DIN', type: 'string' },
  { key: 'aisi', label: 'AISI', type: 'string' },
  { key: 'others', label: 'Outras', type: 'string' },
]

const tabs = [
  { path: '', image: certificate, name: 'Certificados' },
  { path: 'params', image: param, name: 'Parâmetros' },
]

const emptyParam: ParamType = {
  structures: [],
  name: '',
  class: '',
  family: '',
  astm: '',
  uns: '',
  din: '',
  aisi: '',
  others: '',
}

const CertificatesContent: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [refresh, setRefresh] = useState<boolean>(false)
  const [loadingHeader, setLoadingHeader] = useState<boolean>(false)
  const { search } = useOutletContext<TypeMainOutlet>()
  const location = useLocation()
  const navigate = useNavigate()
  const [paramState, setParamState] = useState<ParamType>(emptyParam)
  const [modal, setModal] = useState<boolean>(false)
  const [clickedStructure, setClickedStructure] = useState<number | null>(null)
  const [dynamicCertificateHeaders, setDynamicCertificateHeaders] = useState<Header[]>([
    { key: 'param', label: 'Parâmetro', type: 'string' },
    { key: 'supplier', label: 'Fornecedor', type: 'string' },
    { key: 'customer', label: 'Cliente', type: 'string' },
    { key: 'heatNumber', label: 'Nº da corrida', type: 'string' },
    { key: 'codeOpString', label: 'Nº da OP', type: 'string' },
    { key: 'purchaseOrderNumber', label: 'Nº da ordem de compra', type: 'string' },
    { key: 'standard', label: 'Norma', type: 'string' },
    { key: 'invoiceNumber', label: 'NF', type: 'string' },
    { key: 'type', label: 'Tipo do certificado', type: 'string', selectOptions: [] },
    { key: 'date', label: 'Data de criação', type: 'date' },
  ])

  const tabIndex = location.pathname.includes('/panel/certificates/params') ? 1 : 0
  const isCreateRoute = location.pathname.includes('create') && !location.pathname.includes('edit')
  const isEditRoute = location.pathname.includes('edit')

  const certificateFiltersState = usePersistedFilters('certificates')
  const paramFiltersState = usePersistedFilters('certificate_params')
  const activeFiltersState = tabIndex === 1 ? paramFiltersState : certificateFiltersState

  useEffect(() => {
    if (tabIndex === 0) {
      ApiService.certificates.getTypes()
        .then((response: any) => {
          if (response.data && Array.isArray(response.data)) {
            const options: { value: string; label: string }[] = []
            response.data.forEach((item: any) => {
              if (item.name === 'Origem / Qualidade' || item.id === 1) {
                options.push({ value: 'Origem', label: 'Origem' })
                options.push({ value: 'Qualidade', label: 'Qualidade' })
              } else {
                options.push({ value: item.name, label: item.name })
              }
            })
            setDynamicCertificateHeaders(prev =>
              prev.map(h => h.key === 'type' ? { ...h, selectOptions: options } : h)
            )
          }
        })
        .catch((error: any) => {
          console.error(error)
        })
    }
  }, [tabIndex])

  useEffect(() => {
    setParamState(emptyParam)
  }, [tabIndex])

  const outletContext: CertificateOutletType = {
    setModal,
    param: paramState,
    setParam: setParamState,
    setClickedStructure,
    setLoading,
    appliedFilters: activeFiltersState.appliedFilters,
    search,
    refresh,
    setLoadingHeader,
  }

  return (
    <>
      {isCreateRoute ? (
        <SubHeaderContainer>
          <LeftSide>
            <SectionName>{tabIndex === 1 ? 'Parâmetro da norma' : 'Novo certificado'}</SectionName>
          </LeftSide>
        </SubHeaderContainer>
      ) : (
        <PageHeader
          title={isEditRoute ? (tabIndex === 1 ? 'Parâmetro da norma' : 'Editar certificado') : 'Certificados'}
          tabs={tabs.map((tab) => ({ key: tab.path, label: tab.name, icon: tab.image }))}
          activeTab={tabs[tabIndex].path}
          onTabChange={(key) => {
            if (key !== tabs[tabIndex].path) navigate(`/panel/certificates/${key}`)
          }}
          onReload={() => setRefresh((p) => !p)}
          isLoading={loadingHeader}
          filterHeaders={tabIndex === 1 ? paramTableHeaders : dynamicCertificateHeaders}
          onApplyFilter={activeFiltersState.handleApplyFilter}
          appliedFilters={activeFiltersState.appliedFilters}
          onRemoveFilter={activeFiltersState.handleRemoveFilter}
          onClearAllFilters={activeFiltersState.handleClearAll}
          createButton={{
            label: tabIndex === 0 ? 'Criar certificado' : 'Criar parâmetro',
            onClick: () => navigate(tabIndex === 0 ? '/panel/certificates/create' : '/panel/certificates/params/create'),
          }}
        />
      )}
      {loading === true && <LoadingComponent />}
      <Content>
        <Outlet context={outletContext} />
      </Content>
      {modal &&
      <EditStructureModal
        onClose={() => setModal(false)}
        param={paramState}
        setParam={setParamState}
        clickedStructure={clickedStructure || 0}
      />}
    </>
  )
}
export default CertificatesContent
